import ArrowCircleLeftOutlinedIcon from '@mui/icons-material/ArrowCircleLeftOutlined';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import { Avatar, Box, Divider, IconButton, Stack, ThemeProvider, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/system';
import { gsap } from 'gsap';
import React, { useRef, useState } from 'react';
import { generateTheme } from '../themes';
import { RPMText } from './subComponents/RPMText';

const CustomerStoriesSlice = ({ data }) => {
    const touchStart = useRef(0);
    const touchEnd = useRef(0);

    const { colorTheme = 'white', items = [] } = data || {};

    const theme = generateTheme(colorTheme);
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [activeIndex, setActiveIndex] = useState(0);
    const [mobileShowImage, setMobileShowImage] = useState(false);
    const totalItems = items.length;
    const sliderRef = useRef(null);
    const textLayerRef = useRef(null);

    const animateSlider = (applyChange, dir) => {
        gsap.to(sliderRef.current, {
            opacity: 0,
            x: dir > 0 ? -60 : 60,
            duration: 0.2,
            onComplete: () => {
                applyChange();
                if (textLayerRef.current) gsap.set(textLayerRef.current, { opacity: 1 });
                gsap.fromTo(sliderRef.current,
                    { opacity: 0, x: dir > 0 ? 60 : -60 },
                    { opacity: 1, x: 0, duration: 0.4, ease: 'power3.out' }
                );
            }
        });
    };

    const animateReveal = (applyChange, reverse = false) => {
        applyChange();
        if (textLayerRef.current) {
            gsap.to(textLayerRef.current, {
                opacity: reverse ? 1 : 0,
                duration: reverse ? 0.35 : 0.3,
                ease: reverse ? 'power2.out' : 'power2.in'
            });
        }
    };

    const handlePrev = () => {
        if (isMobile && mobileShowImage) {
            animateReveal(() => setMobileShowImage(false), true);
            return;
        }
        if (totalItems < 2) return;
        const newIdx = activeIndex > 0 ? activeIndex - 1 : totalItems - 1;
        const showImageNext = isMobile && !!items[newIdx]?.customerMainImage;
        animateSlider(() => {
            setActiveIndex(newIdx);
            setMobileShowImage(showImageNext);
        }, -1);
    };

    const handleNext = () => {
        if (isMobile && !mobileShowImage && items[activeIndex]?.customerMainImage) {
            animateReveal(() => setMobileShowImage(true));
            return;
        }
        if (totalItems < 2) return;
        const newIdx = activeIndex < totalItems - 1 ? activeIndex + 1 : 0;
        animateSlider(() => {
            setActiveIndex(newIdx);
            setMobileShowImage(false);
        }, 1);
    };

    const preloadUrls = [];
    items.forEach(item => {
        const main = item?.customerMainImage;
        if (main) preloadUrls.push(main.url || main);
        const logo = item?.companyLogo;
        if (logo) preloadUrls.push(logo.url || logo);
        const photo = item?.customerPhoto;
        if (photo) preloadUrls.push(photo.url || photo);
    });

    const handleTouchStart = (e) => { touchStart.current = e.targetTouches[0].clientX; };
    const handleTouchMove = (e) => { touchEnd.current = e.targetTouches[0].clientX; };
    const handleTouchEnd = () => {
        if (touchStart.current - touchEnd.current > 30) handleNext();
        else if (touchStart.current - touchEnd.current < -30) handlePrev();
    };

    const getInitials = (name) => {
        if (!name) return '';
        const stripped = name.replace(/<[^>]*>/g, '').trim();
        const parts = stripped.split(/\s+/);
        return parts.map(p => p[0] || '').slice(0, 2).join('').toUpperCase();
    };

    if (!items.length) return null;

    const activeItem = items[activeIndex] || {};
    const hasMultiple = totalItems > 1;
    const accent = theme.palette.primary?.main || '#5C33D7';
    const cardBg = '#FFFEFB';

    const arrowSx = {
        color: accent,
        fontSize: { xs: '2.5rem', md: '3rem' },
        transition: 'opacity 0.15s ease-in-out',
        '&:hover': { opacity: 0.75 }
    };

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: (theme) => theme.palette.background.default,
                minHeight: { xs: 'calc(100dvh - 120px)', md: 'auto' },
                py: { xs: 2.5, md: 8 },
                px: { xs: 2, md: 4 }
            }}>
                <Box aria-hidden='true' sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', opacity: 0, pointerEvents: 'none', top: 0, left: 0 }}>
                    {preloadUrls.map((url, idx) => (
                        typeof url === 'string' ? <img key={idx} src={url} alt='' loading='eager' decoding='async' /> : null
                    ))}
                </Box>
                <Box sx={{
                    width: '100%',
                    maxWidth: '1200px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: { md: 2, lg: 3 }
                }}>
                    {hasMultiple && (
                        <IconButton
                            onClick={handlePrev}
                            aria-label='Previous customer story'
                            sx={{ display: { xs: 'none', md: 'flex' }, flexShrink: 0 }}
                        >
                            <ArrowCircleLeftOutlinedIcon sx={arrowSx} />
                        </IconButton>
                    )}

                    <Box
                        ref={sliderRef}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            height: { xs: 'calc(100dvh - 240px)', md: 540, lg: 580 },
                            maxHeight: { xs: 640, md: 'none' },
                            bgcolor: cardBg,
                            borderRadius: { xs: '1.25rem', md: '1.5rem' },
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: 'stretch',
                            position: 'relative'
                        }}
                    >
                        <Stack ref={textLayerRef} sx={{
                            flex: 1,
                            minWidth: 0,
                            p: { xs: 2.5, md: 4, lg: 5 },
                            justifyContent: 'space-between',
                            gap: 3
                        }}>
                            <Stack spacing={{ xs: 2, md: 3 }}>
                                {activeItem.companyLogo && (
                                    <Box
                                        sx={{
                                            '& img': {
                                                width: 'auto',
                                                height: '32px',
                                                maxWidth: '180px',
                                                objectFit: 'contain',
                                                display: 'block',
                                                filter: 'grayscale(1)',
                                                opacity: 0.7,
                                                transition: 'filter 0.3s ease, opacity 0.3s ease'
                                            },
                                            '&:hover img': {
                                                filter: 'grayscale(0)',
                                                opacity: 1
                                            }
                                        }}
                                    >
                                        <img
                                            src={activeItem.companyLogo.url || activeItem.companyLogo}
                                            alt={activeItem.companyLogo.alt || 'Company logo'}
                                        />
                                    </Box>
                                )}
                                {activeItem.quoteHeadline && (
                                    <RPMText
                                        content={activeItem.quoteHeadline}
                                        sx={{
                                            color: 'rgb(59, 59, 44)',
                                            fontFamily: 'Kanit, sans-serif',
                                            fontWeight: 500,
                                            fontSize: { xs: '1.5rem', md: '1.75rem', lg: '2rem' },
                                            lineHeight: 1.2,
                                            '& p, & h1, & h2, & h3, & h4, & h5': { m: 0, fontFamily: 'inherit', fontWeight: 'inherit', fontSize: 'inherit', lineHeight: 'inherit', color: 'inherit', textTransform: 'none' }
                                        }}
                                    />
                                )}
                                {activeItem.testimony && (
                                    <RPMText
                                        content={activeItem.testimony}
                                        sx={{
                                            fontFamily: 'Roboto, sans-serif',
                                            fontSize: { xs: '1.05rem', md: '1.15rem' },
                                            lineHeight: 1.6,
                                            '&::before': { content: '"\\201C"', mr: '0.1em' },
                                            '&::after': { content: '"\\201D"', ml: '0.1em' },
                                            '& p, & blockquote': { m: 0, display: 'inline' }
                                        }}
                                    />
                                )}
                            </Stack>

                            <Stack direction='row' alignItems='center' spacing={2}>
                                <Avatar
                                    src={activeItem.customerPhoto?.url || activeItem.customerPhoto}
                                    alt={activeItem.customerName ? activeItem.customerName.replace(/<[^>]*>/g, '') : ''}
                                    sx={{
                                        width: { xs: 60, md: 72 },
                                        height: { xs: 60, md: 72 },
                                        bgcolor: alpha(accent, 0.15),
                                        color: accent,
                                        fontFamily: 'Kanit, sans-serif',
                                        fontSize: { xs: '1.1rem', md: '1.25rem' },
                                        fontWeight: 600,
                                        flexShrink: 0
                                    }}
                                >
                                    {getInitials(activeItem.customerName)}
                                </Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                    <RPMText
                                        content={activeItem.customerName}
                                        sx={{
                                            fontFamily: 'Roboto, sans-serif',
                                            fontWeight: 700,
                                            '& p, & h5': { m: 0, fontFamily: 'inherit', fontWeight: 'inherit', fontSize: 'inherit' }
                                        }}
                                    />
                                    <RPMText
                                        content={activeItem.customerPosition}
                                        sx={{
                                            fontFamily: 'Roboto, sans-serif',
                                            opacity: 0.7,
                                            fontSize: '0.9rem',
                                            '& p': { m: 0 }
                                        }}
                                    />
                                </Box>
                            </Stack>
                        </Stack>

                        {activeItem.customerMainImage && (
                            <Box sx={{
                                flexShrink: 0,
                                alignSelf: 'stretch',
                                width: { md: '42%', lg: '44%' },
                                display: { xs: 'none', md: 'flex' },
                                position: 'relative',
                                alignItems: 'flex-end',
                                justifyContent: 'center',
                                pt: { md: 4, lg: 5 },
                                px: { md: 4, lg: 5 },
                                pb: 0
                            }}>
                                <img
                                    src={activeItem.customerMainImage.url || activeItem.customerMainImage}
                                    alt={activeItem.customerMainImage.alt || 'Customer card'}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        width: 'auto',
                                        height: 'auto',
                                        objectFit: 'contain',
                                        display: 'block'
                                    }}
                                />
                            </Box>
                        )}

                        {isMobile && activeItem.customerMainImage && (
                            <Box
                                key={activeIndex}
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    bgcolor: 'transparent',
                                    display: { xs: 'flex', md: 'none' },
                                    alignItems: 'flex-end',
                                    justifyContent: 'center',
                                    pt: 3,
                                    px: 3,
                                    pb: 0,
                                    transform: mobileShowImage ? 'translate3d(0, 0, 0)' : 'translate3d(0, 100%, 0)',
                                    transition: 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
                                    willChange: 'transform',
                                    zIndex: 2
                                }}
                            >
                                <img
                                    src={activeItem.customerMainImage.url || activeItem.customerMainImage}
                                    alt={activeItem.customerMainImage.alt || 'Customer card'}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        width: 'auto',
                                        height: 'auto',
                                        objectFit: 'contain',
                                        display: 'block'
                                    }}
                                />
                            </Box>
                        )}
                    </Box>

                    {hasMultiple && (
                        <IconButton
                            onClick={handleNext}
                            aria-label='Next customer story'
                            sx={{ display: { xs: 'none', md: 'flex' }, flexShrink: 0 }}
                        >
                            <ArrowCircleRightOutlinedIcon sx={arrowSx} />
                        </IconButton>
                    )}
                </Box>

                {hasMultiple && (
                    <Box sx={{
                        display: { xs: 'flex', md: 'none' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5,
                        mt: 1.5
                    }}>
                        <IconButton
                            onClick={handlePrev}
                            aria-label='Previous customer story'
                            size='small'
                            sx={{ p: 0.5 }}
                        >
                            <ArrowCircleLeftOutlinedIcon sx={{ color: accent, fontSize: '2rem', transition: 'opacity 0.15s', '&:hover': { opacity: 0.75 } }} />
                        </IconButton>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            {items.map((_, idx) => (
                                <Box
                                    key={idx}
                                    role='button'
                                    aria-label={`Go to customer story ${idx + 1}`}
                                    onClick={() => {
                                        if (idx === activeIndex && !mobileShowImage) return;
                                        if (idx === activeIndex && mobileShowImage) {
                                            animateReveal(() => setMobileShowImage(false), true);
                                            return;
                                        }
                                        const dir = idx >= activeIndex ? 1 : -1;
                                        animateSlider(() => {
                                            setActiveIndex(idx);
                                            setMobileShowImage(false);
                                        }, dir);
                                    }}
                                    sx={{
                                        width: activeIndex === idx ? 28 : 14,
                                        height: 5,
                                        borderRadius: 3,
                                        bgcolor: activeIndex === idx ? accent : alpha(theme.palette.text.primary, 0.2),
                                        cursor: 'pointer',
                                        transition: 'width 0.3s ease-in-out, background-color 0.2s ease-in-out'
                                    }}
                                />
                            ))}
                        </Box>

                        <IconButton
                            onClick={handleNext}
                            aria-label='Next customer story'
                            size='small'
                            sx={{ p: 0.5 }}
                        >
                            <ArrowCircleRightOutlinedIcon sx={{ color: accent, fontSize: '2rem', transition: 'opacity 0.15s', '&:hover': { opacity: 0.75 } }} />
                        </IconButton>
                    </Box>
                )}

                {hasMultiple && (
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', alignItems: 'center', mt: 3, gap: 1.25 }}>
                        {items.map((_, idx) => (
                            <Box
                                key={idx}
                                role='button'
                                aria-label={`Go to customer story ${idx + 1}`}
                                onClick={() => {
                                    if (idx === activeIndex) return;
                                    const dir = idx >= activeIndex ? 1 : -1;
                                    animateSlider(() => setActiveIndex(idx), dir);
                                }}
                                sx={{
                                    width: activeIndex === idx ? 44 : 20,
                                    height: 7,
                                    borderRadius: 4,
                                    bgcolor: activeIndex === idx ? accent : alpha(theme.palette.text.primary, 0.15),
                                    cursor: 'pointer',
                                    transition: 'width 0.3s ease-in-out, background-color 0.2s ease-in-out'
                                }}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        </ThemeProvider>
    );
};

export default CustomerStoriesSlice;
